import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIconButton } from '@angular/material/button'
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox'
import { MatDialog } from '@angular/material/dialog'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTab, MatTabContent, MatTabGroup } from '@angular/material/tabs'
import { MatTooltip } from '@angular/material/tooltip'
import { Router } from '@angular/router'
import {
  ElderBasicPaneLayoutComponent,
  ElderFormFieldLabelDirective,
  ElderIntervalInputComponent,
  ElderLocalDateInputComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderTabDirective,
  ElderTabGroupRoutingDirective,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable, PartialObserver, Subscription } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { truthyPredicate } from '../../../../../functional.util'
import { PackageBrowserComponent } from '../../../packages/package-browser/package-browser.component'
import { WorkBatchBrowserComponent } from '../../batches/work-batch-browser/work-batch-browser.component'
import { UtilisationIntervalLinkComponent } from '../../utilisation/utilisation-interval-link/utilisation-interval-link.component'
import { WorkItem } from '../../work-items/work-item'
import { WorkItemTableComponent } from '../../work-items/work-item-table/work-item-table.component'
import { WorkItemService } from '../../work-items/work-item.service'
import { WorkOrderCommand, WorkOrderCommandType } from '../commands/WorkOrderCommand'
import { OrderPrioritySelectDirective } from '../order-priority-select.directive'
import { WorkClearanceSelectDirective } from '../work-clearance-select.directive'
import {
  LeadTimeMeta,
  NewWorkOrder,
  OrderCompletionState,
  WorkOrder,
  WorkOrderOverview,
} from '../work-order'
import { WorkOrderDeliveryComponent } from '../work-order-delivery/work-order-delivery.component'
import { WorkOrderItemTransferComponent } from '../work-order-item-transfer/work-order-item-transfer.component'
import { WorkOrderOverviewComponent } from '../work-order-overview/work-order-overview.component'
import { WorkOrderUpdate } from '../work-order-update'
import { WorkOrderCommandResult, WorkOrderService } from '../work-order.service'

interface HintTranslateParams {
  actualDays: number
  scheduleNames: string
  scheduleSkipDays: number
}

@Component({
  selector: 'app-work-order-full',
  templateUrl: './work-order-full.component.html',
  styleUrls: ['./work-order-full.component.scss'],
  imports: [
    MatIconButton,
    MatTooltip,
    MatIcon,
    UtilisationIntervalLinkComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatFormField,
    ElderFormFieldLabelDirective,
    MatLabel,
    MatInput,
    ElderSelectComponent,
    WorkClearanceSelectDirective,
    OrderPrioritySelectDirective,
    ElderLocalDateInputComponent,
    ElderIntervalInputComponent,
    MatCheckbox,
    WorkOrderDeliveryComponent,
    WorkOrderOverviewComponent,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    ElderTabDirective,
    MatTabContent,
    WorkItemTableComponent,
    WorkBatchBrowserComponent,
    PackageBrowserComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
    ElderPaneTitleComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
  ],
})
export class WorkOrderFullComponent {
  private readonly router = inject(Router)
  private readonly workOrderService = inject(WorkOrderService)
  private readonly workItemService = inject(WorkItemService)
  private readonly toastService = inject(ElderToastService)
  private dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly workOrder$ = new BehaviorSubject<WorkOrder>(null)
  public readonly overview$ = new BehaviorSubject<WorkOrderOverview>(null)

  @Output()
  public readonly deleted: EventEmitter<void> = new EventEmitter()

  @Output()
  public readonly reloadWorkOrdersForProdOrder: EventEmitter<string> = new EventEmitter()

  public readonly canDelete$: Observable<boolean>
  public readonly involvedProcessPlans$: Observable<string[]>
  private itemUpdateSub: Subscription

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.canDelete$ = this.overview$.pipe(
      map((overview) => overview?.aggregate?.total?.numberOfItems === 0)
    )

    this.involvedProcessPlans$ = this.overview$.pipe(
      map((overview) => (overview?.aggregate ? Object.keys(overview.aggregate.processPlan) : []))
    )

    this.workOrder$
      .pipe(takeUntilDestroyed(), filter(truthyPredicate))
      .subscribe((workOrder) => this.loadOverview(workOrder.id))
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set workOrder(workOrder: WorkOrder) {
    if (workOrder) {
      this.workOrder$.next(workOrder)

      if (!this.itemUpdateSub) {
        this.itemUpdateSub = this.workItemService.dataChanged.subscribe(() =>
          this.reloadWorkOrder(workOrder.id)
        ) // needed for WorkOrderOverview upate
      }
    }
  }

  public get workOrder(): WorkOrder {
    return this.workOrder$.getValue()
  }

  public get overview(): WorkOrderOverview {
    return this.overview$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public previewItem(item: WorkItem) {
    this.router.navigate([{ outlets: { side: ['work-items', item.id] } }], {
      preserveFragment: true,
    })
  }

  /**
   * Delete the given items
   */
  public deleteAllItems(items: WorkItem[]): void {
    /*
    this.workItemService.deleteAll(items)
      .subscribe(
        () => this.logger.info('Deleted ' + items.length + ' items.'),
        err => this.toastService.error(
          'messages.deleting_failed_specific',
          {subject: 'Item(s)'},
          err
        )
      );*/
  }

  public saveWorkOrder(): void {
    const patch = new WorkOrderUpdate(this.workOrder.id)
    // Delivery assignment patches are executed in the work-order-delivery-assigner component and do not have to be included here
    patch.releaseAt = this.workOrder.releaseAt
    patch.scheduleInterval = this.workOrder.scheduleInterval
    patch.disableAutoScheduling = this.workOrder.disableAutoScheduling
    patch.orderPriority = this.workOrder.orderPriority
    patch.clearance = this.workOrder.clearance
    this.requestPatch(patch)
  }

  public deleteWorkOrder(): void {
    this.workOrderService.delete(this.workOrder).subscribe({
      next: (_) => this.deleted.emit(),
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  public splitWorkOrder(): void {
    this.workOrderService
      .findOrCreateEmptyWorkOrder(
        new NewWorkOrder(
          this.workOrder.prodOrderId,
          this.workOrder.manufactureFacility,
          this.workOrder.collectingFacility
        )
      )
      .subscribe((wo) => {
        if (wo) {
          this.startItemTransfer(this.workOrder, wo)
        } else {
          this.toastService.error('Could not split work order!', '')
        }
      })
  }

  public startItemTransfer(orderA: WorkOrder, orderB: WorkOrder): void {
    const dialogRef = this.dialog.open(WorkOrderItemTransferComponent, {
      maxWidth: '100vw',
      width: '98vw',
      height: '95vh',
      disableClose: true,
      autoFocus: false,
      data: {
        orderA: orderA,
        orderB: orderB,
      },
    })

    dialogRef.afterClosed().subscribe(() => {
      this.reloadWorkOrdersForProdOrder.emit(orderA.prodOrderId)
      // TODO this does not work for the item-table, since the table checks, if the order actually changed
      this.reloadWorkOrder(orderA.id)
    })
  }

  public onAutoScheduleChange($event: MatCheckboxChange) {
    this.workOrder.disableAutoScheduling = !$event.checked
  }

  public goToProdOrder(): void {
    this.router.navigate(['app', 'prod-orders', this.workOrder.prodOrderId], {
      fragment: 'prodOrderDetailTab=' + this.workOrder.id,
      preserveFragment: true,
    })
  }

  public overrideDisabled(workOrder: WorkOrder) {
    const missingDueAt = !workOrder?.dueAt
    return missingDueAt || workOrder.completionState != OrderCompletionState.OPEN
  }

  validateScheduleInterval(workOrder: WorkOrder) {
    const id = workOrder?.id
    if (id) {
      this.workOrderService
        .executeCommand(id, <WorkOrderCommand>{
          type: WorkOrderCommandType.EVALUATE_SCHEDULE_VALIDATION,
        })
        .subscribe(this.workOrderService.commandSubscriber(this.toastService))
    }
  }

  assignProdRoutes(workOrder: WorkOrder) {
    const id = workOrder?.id
    if (id) {
      this.workOrderService
        .executeCommand(id, <WorkOrderCommand>{
          type: WorkOrderCommandType.ASSIGN_PROD_ROUTES,
        })
        .subscribe(this.workOrderService.commandSubscriber(this.toastService))
    }
  }

  getHintKey(leadTimeMeta: LeadTimeMeta): string {
    return leadTimeMeta.searchFailed ? 'processing.leadTimeHintFallback' : 'processing.leadTimeHint'
  }

  getHintTranslateParams(workOrder: WorkOrder): HintTranslateParams {
    const leadTimeMeta = workOrder.leadTimeMeta
    const actualDays = !leadTimeMeta.searchFailed
      ? leadTimeMeta.leadTimeDays
      : leadTimeMeta.fallBackDays
    return {
      actualDays,
      scheduleNames: this.getInvolvedSchedules(leadTimeMeta),
      scheduleSkipDays: leadTimeMeta.scheduleSkipDays,
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private reloadWorkOrder(workOrderId: string): void {
    this.workOrderService.findById(workOrderId).subscribe({
      next: (workOrder) => (this.workOrder = workOrder),
      error: (err) => this.toastService.error('Failed to load WorkOrder!', err),
    })
  }

  private loadOverview(workOrderId: string): void {
    this.workOrderService.getOverview(workOrderId).subscribe({
      next: (overview) => this.overview$.next(overview),
      error: (err) => this.toastService.error('Failed to load WorkOrderOverview!', err),
    })
  }

  private requestPatch(patch: WorkOrderUpdate): void {
    this.log.debug('Sending patch request:', patch)

    this.workOrderService.patch(patch).subscribe({
      next: (order) => {
        this.toastService.info('messages.persisting_successful')
        this.workOrder = order
      },
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }

  private getInvolvedSchedules(leadTimeMeta: LeadTimeMeta) {
    return leadTimeMeta.involvedSchedules.map((schedule) => schedule.name).join(', ')
  }
}
